import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { EfoEgiService } from './egi.service';
import * as d3 from 'd3';
import * as d3scale from 'd3-scale';
import * as d3zoom from 'd3-zoom';
import * as d3drag from 'd3-drag';
import * as d3geo from 'd3-geo';
import * as d3selection from 'd3-selection';
import * as topojson from 'topojson';
import { each } from 'async-es';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EfoMapService {
  public marketRadius = 15;
  public radiusSliderConfig: any = {
    connect: true,
    start: this.marketRadius,
    //step: 10,
    pips: { mode: 'count', values: 6, density: 4, stepped: true },
    range: {
      min: 0,
      max: 50,
    },
  };
  public selectedStates = ['GA'];
  public ypDealers: any = [];
  public ypStateData: any = [];
  public ypZipData: any = [];
  public ypZipDataWithDistance: any = [];
  public dataByZip: any = {};
  public zipData: any = [];
  private stateTopoJson: any;
  private topoFeature: any;
  public colorBlue: any;
  public mapDataRadii: any = [];
  private mapDiv: string;
  private colorOrange: any;
  private mapData: any = [];

  constructor(private efoEgi: EfoEgiService) {}

  distFrom = (lat1, lng1, lat2, lng2) => {
    const earthRadius = 3958.75;
    let dLat = ((lat2 - lat1) * Math.PI) / 180;
    let dLng = ((lng2 - lng1) * Math.PI) / 180;
    let sindLat = Math.sin(dLat / 2);
    let sindLng = Math.sin(dLng / 2);
    let a =
      Math.pow(sindLat, 2) +
      Math.pow(sindLng, 2) *
        Math.cos(Math.PI / 180) *
        Math.cos((lat2 * Math.PI) / 180);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let dist = earthRadius * c;
    return dist;
  };

  initChart(states, options, callback) {
    states = _.isArray(states)
      ? _.map(states, (d) => {
          return d.toUpperCase();
        })
      : _.map(states.split(','), (d) => {
          return d.toUpperCase();
        });
    this.selectedStates = states;
    this.mapDiv = options.mapEle;
    this.handlers = _.extend(this.handlers, options.handlers);
    console.log('initChart', states);
    if (states.length > 1) {
      const stateData = [];
      const topoFeatureData = [];
      each(
        states,
        (state, cb) => {
          console.log('initChart getting', state);
          this.efoEgi
            .getData(
              '/elevate/geojson/' + state.toLowerCase() + '_zip_codes.json',
              {},
            )
            .subscribe((data) => {
              stateData.push(data);
              topoFeatureData.push(
                topojson.feature(data, data.objects.zipcodes),
              );
              cb();
            });
        },
        () => {
          this.topoFeature = {
            type: 'FeatureCollection',
            features: _.flatten(_.map(topoFeatureData, 'features')),
          };
          this.colorBlue = d3scale
            .scaleLinear()
            .domain([100, 50, 1])
            .range(<any>d3.schemeBlues[3]);
          this.colorOrange = d3scale
            .scaleLinear()
            .domain([100, 50, 0])
            .range(<any>d3['schemeOranges'][3]);
          this.getYpZipData(() => {
            if (_.isFunction(callback)) {
              callback();
            }
          });
        },
      );
    } else {
      //if (true || _.isEmpty(this.stateTopoJson)){
      let topoUrl =
        environment.baseApiUrl +
        '/elevate/geojson/' +
        states[0].toLowerCase() +
        '_zip_codes.json';
      d3.json(topoUrl)['then']((data) => {
        console.log('state.topojson', data);
        this.stateTopoJson = data;
        this.topoFeature = topojson.feature(
          this.stateTopoJson,
          this.stateTopoJson.objects.zipcodes,
        );
        this.colorBlue = d3scale
          .scaleLinear()
          .domain([100, 50, 1])
          .range(<any>d3.schemeBlues[3]);
        this.colorOrange = d3scale
          .scaleLinear()
          .domain([100, 50, 0])
          .range(<any>d3['schemeOranges'][3]);
        this.getYpZipData(() => {
          if (_.isFunction(callback)) {
            callback();
          }
        });
      });
    }
  }

  public g: any;
  private mapProjection: any;
  private mapPath: any;
  private xAxis: any;
  private yAxis: any;
  private svgStateMap: any;
  private zoom: any;
  private width = 620;
  private height = 760;
  public hoverData: any = {};
  public hoverLocation: any = {};
  private handlers = {
    click: (d) => {},
    mouseover: (d) => {},
    mouseout: (d) => {},
  };

  chartO(): Observable<any> {
    console.log('in chart');
    this.mapProjection = d3geo
      .geoAlbers()
      .fitSize([this.width, this.height], this.topoFeature);
    d3.select(this.mapDiv).selectAll('*').remove();
    this.svgStateMap = d3
      .select(this.mapDiv)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height);

    this.mapPath = d3geo.geoPath(this.mapProjection);

    this.g = this.svgStateMap.append('g');

    // load and display the World
    this.g
      .attr('class', 'zips')
      .selectAll('path')
      .data(<any>this.topoFeature.features)
      .enter()
      .append('path')
      .attr('d', this.mapPath)
      // .attr("stroke-width", 15) //default to gray
      // .attr("stroke", "red") //default to gray
      .attr('id', (d) => {
        return 'zm' + d.properties.zip;
      })
      ['join']('path')
      .attr('fill', '#cccccc') //default to gray
      .on('click', (e, d: any) => {
        console.log('click event', d);
        this.handlers.click(d);
        //this.click(d);
      })
      //.on('mouseover',d=>{console.log(d);})
      .on('mouseover', (e, d) => {
        console.log(
          'mouseover zip',
          d.properties.zip,
          this.dataByZip[d.properties.zip],
        );
        const data = this.dataByZip[d.properties.zip];
        data.consumer_data = Math.floor(
          (data.population *
            ((+(d.properties.zip[4] + d.properties.zip[4]) * 20) / 100 + 60)) /
            100,
        ); //60-80% of population

        this.hoverData = data;
      });
    // .on('mouseout',d => { })

    // let colorByZip = _.reduce(this.mapData,(o,d,k) => {o[k]=this.colorBlue(d); return o;},{});
    // this.fillZips(colorByZip);

    this.zoom = d3zoom
      .zoom()
      .scaleExtent([1, 8])
      .on('zoom', (e) => {
        //console.log('zoom',e);
        this.g.selectAll('path,circle').attr('transform', (<any>e).transform);
      });

    this.svgStateMap.call(this.zoom).on('wheel.zoom', null); //this disables scroll zoom
    return of({ msg: 'finished chart' });
  }

  chart = (callback?) => {
    console.log('in chart');

    // this.mapProjection = d3geo.geoMercator()
    //   .center([0, 5 ])
    //   .scale(150)
    //   .rotate([-180,0]);
    this.mapProjection = d3geo
      .geoAlbers()
      //.rotate([74 + 30 / 60, -38 - 50 / 60])
      .fitSize([this.width, this.height], this.topoFeature);

    this.svgStateMap = d3
      .select(this.mapDiv)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height);

    // const path = d3geo.geoPath()
    //   .projection(this.mapProjection);
    this.mapPath = d3geo.geoPath(this.mapProjection);

    this.g = this.svgStateMap.append('g');

    // load and display the World
    this.g
      .attr('class', 'zips')
      .selectAll('path')
      .data(<any>this.topoFeature.features)
      .enter()
      .append('path')
      .attr('d', this.mapPath)
      // .attr("stroke-width", 15) //default to gray
      // .attr("stroke", "red") //default to gray
      .attr('id', (d) => {
        return 'zm' + d.properties.zip;
      })
      ['join']('path')
      .attr('fill', '#cccccc') //default to gray
      .on('click', (e, d: any) => {
        console.log('click event', d);
        this.handlers.click(d);
        //this.click(d);
      })
      //.on('mouseover',d=>{console.log(d);})
      .on('mouseover', (e, d) => {
        console.log(
          'mouseover zip',
          d.properties.zip,
          this.dataByZip[d.properties.zip],
        );
        const data = this.dataByZip[d.properties.zip];
        data.consumer_data = Math.floor(
          (data.population *
            ((+(d.properties.zip[4] + d.properties.zip[4]) * 20) / 100 + 60)) /
            100,
        ); //60-80% of population

        this.hoverData = data;
      });
    // .on('mouseout',d => { })

    // let colorByZip = _.reduce(this.mapData,(o,d,k) => {o[k]=this.colorBlue(d); return o;},{});
    // this.fillZips(colorByZip);

    this.zoom = d3zoom
      .zoom()
      .scaleExtent([1, 8])
      .on('zoom', (e) => {
        //console.log('zoom',e);
        this.g.selectAll('path,circle').attr('transform', (<any>e).transform);
      });

    this.svgStateMap.call(this.zoom).on('wheel.zoom', null); //this disables scroll zoom
    if (_.isFunction(callback)) {
      callback();
    }
  };

  public selectedZips = [];
  public audienceTotals = {
    zip_count: 0,
    city_count: 0,
    population: 0,
    yp_estimate: 0,
  };
  public selectedZip = '';

  click = (d, callback) => {
    //active = clickedState
    console.log('click', d);
    let id;
    if (_.has(d, 'properties.zip')) {
      id = d.properties.zip;
    } else {
      id = d.zip;
    }
    this.getZipData(id, (err, result) => {
      _.each(result, function (d) {
        if (_.isNull(d.total_records)) {
          d.total_records = d.population * 0.4;
        }
      });
      this.zipData = result;
      this.setMarketRadius(this.marketRadius);
      // let closeZips = _.filter(result,(d) => {return d.distance < this.marketRadius;});
      // this.mapData = _.reduce(<any>closeZips, function (o,d,i) {
      //   o[d.zip] = (+i*100)/closeZips.length;
      //   return o;
      // }, {});
    });
    //reset colors on all
    //this.g.selectAll(".active").classed("active", false);
    //clear all zips
    this.selectedZips = [];
    this.selectedZip = id;
    if (_.indexOf(this.selectedZips, id) == -1) {
      this.selectedZips.push(id);
      d3.select('#zm' + id).classed('active', true);
      d3.select('#zmm' + id).classed('selected', true);
      d3.select('#zr' + id).classed('selected', true);
      d3.select('#zp' + id).classed('selected', true);
      d3.select('#zd' + id).classed('selected', true);
    } else {
      _.pull(this.selectedZips, id);
      d3.select('#zm' + id).classed('active', false);
      d3.select('#zmm' + id).classed('selected', false);
      d3.select('#zr' + id).classed('selected', false);
      d3.select('#zp' + id).classed('selected', false);
      d3.select('#zd' + id).classed('selected', false);
    }
    console.log('selectedZips', this.selectedZips);
    let selectedYpData = _.filter(this.zipData, (d) => {
      return _.indexOf(this.selectedZips, d.zip) > -1;
    });
    if (_.isFunction(callback)) {
      callback();
    }
    return true;
    this.audienceTotals = {
      zip_count: selectedYpData.length,
      city_count: _.uniq(_.map(selectedYpData, 'city')).length,
      population: _.sum(_.map(selectedYpData, 'population')),
      yp_estimate: _.sum(_.map(selectedYpData, 'total_records')),
    };
    this.selectedCitiesStr = _.uniq(_.map(selectedYpData, 'city')).join(', ');

    //this.getConsumersByZip(id,'A');
    // this.rows = [];
    // this.onScroll(0);
    //this.getConsumersByZip(id,'A');
  };

  getZipData(zipCode, callback?): void {
    this.efoEgi
      .getData('/yp/getZipData', { zip: zipCode })
      .subscribe((results) => {
        this.ypZipDataWithDistance = _.sortBy(results, 'distance');

        if (_.isFunction(callback)) {
          callback(null, _.sortBy(results, 'distance'));
        }
      });
  }

  getYpZipData(callback): void {
    this.efoEgi
      .getData('/yp/getYpSummaryByZip', { state: this.selectedStates })
      .subscribe((ypZipData) => {
        this.ypZipData = ypZipData;
        this.dataByZip = _.keyBy(ypZipData, 'zip');
        this.mapData = _.reduce(
          <any>this.ypZipData,
          function (o, d, i) {
            o[d.zip] =
              d.num_pages == 0 ? 0 : (d.pages_received * 100) / d.num_pages;
            return o;
          },
          {},
        );
        callback();
      });
  }

  public selectedZipsStr = '';
  public selectedCitiesStr = '';
  public marketRadiusZips = [];

  setMarketRadius(event, callback?): void {
    this.marketRadius = event;
    console.log('selected distance', this.marketRadius, event);
    let closeZips = _.filter(this.ypZipDataWithDistance, (d) => {
      return d.distance < this.marketRadius;
    });
    console.log('# zips = %s', closeZips.length);
    this.mapDataRadii = _.reduce(
      <any>closeZips,
      function (o, d, i) {
        o[d.zip] = (+i * 100) / closeZips.length;
        return o;
      },
      {},
    );

    this.audienceTotals = {
      zip_count: closeZips.length,
      city_count: _.uniq(_.map(closeZips, 'city')).length,
      population: _.sum(_.map(closeZips, 'population')),
      yp_estimate: _.sum(_.map(closeZips, 'total_records')),
    };
    this.selectedZips = _.map(closeZips, 'zip');
    this.selectedCitiesStr = _.uniq(_.map(closeZips, 'city')).join(', ');
    this.selectedZipsStr = _.uniq(_.map(closeZips, 'zip')).join(', ');
    this.marketRadiusZips = closeZips;
    let colorByZip = _.reduce(
      this.mapDataRadii,
      (o, d, k) => {
        o[k] = this.colorBlue(d);
        return o;
      },
      {},
    );
    this.fillZips(colorByZip);
    if (_.isFunction(callback)) {
      callback();
    }
    return;
  }

  plotLocations(locationData, options?, callback?): void {
    let ptColor = _.isUndefined(options.color) ? 'red' : options.color;
    this.g
      .selectAll('circle')
      .data(locationData)
      .enter()
      .append('circle')
      .attr('id', (d) => {
        return _.has(d, 'id') ? d.id : false;
      })
      .attr('class', (d) => {
        return _.has(d, 'class') ? d.class.join(' ') : false;
      })
      .attr('cx', (d) => {
        return this.mapProjection([d.longitude, d.latitude])[0];
      })
      .attr('cy', (d) => {
        return this.mapProjection([d.longitude, d.latitude])[1];
      })
      .attr('r', '2px')
      .attr('fill', (d) => {
        return _.has(d, 'color') ? d.color : ptColor;
      })
      .on('click', (e, d: any) => {
        if (
          _.has(options, 'handlers.click') &&
          _.isFunction(options.handlers.click)
        ) {
          options.handlers.click(d, e);
        }
      })
      .on('mouseover', (e, d: any) => {
        console.log(
          'mouseover point2',
          d,
          _.has(options, 'handlers.mouseover'),
          _.isFunction(options.handlers.mouseover),
        );
        this.hoverLocation = d;
        if (
          _.has(options, 'handlers.mouseover') &&
          _.isFunction(options.handlers.mouseover)
        ) {
          options.handlers.mouseover(d, e);
        }
      })
      .on('mouseout', (e, d: any) => {
        console.log('mouseout2', d);
        if (
          _.has(options, 'handlers.mouseout') &&
          _.isFunction(options.handlers.mouseout)
        ) {
          options.handlers.mouseout(d);
        }
      });
  }

  plotRegion(locationData, options?, callback?): void {
    let ptColor = _.has(options, 'color') ? options.color : 'red';
    this.g
      .selectAll('circle')
      .data(locationData)
      .enter()
      .append('circle')
      .attr('cx', (d) => {
        console.log(d, this.mapProjection(d));
        return this.mapProjection([d.longitude, d.latitude])[0];
      })
      .attr('cy', (d) => {
        return this.mapProjection([d.longitude, d.latitude])[1];
      })
      .attr('r', '2px')
      .attr('fill', ptColor)
      .on('click', (e, d) => {})
      .on('mouseover', (e, d: any) => {
        console.log('mouseover point', d);
      })
      .on('mouseout', (e, d: any) => {
        console.log('mouseout', d);
      });
  }

  plotRadius(centerPtData, options?, callback?): void {
    let radiusColor = _.has(options, 'color') ? options.color : 'red';
    let circumference = 6371000 * Math.PI * 2;
    let milesInMeters = options.radiusMiles * 1609.34;
    let angle = (milesInMeters / circumference) * 360;
    let circle = d3geo
      .geoCircle()
      .center([centerPtData.longitude, centerPtData.latitude])
      .radius(angle);
    this.g
      .append('path')
      .attr('d', this.mapPath(circle()))
      //.attr("fill", radiusColor)
      .attr('fill', 'none')
      .attr('stroke', radiusColor)
      .attr('stroke-width', 2)
      .on('click', (e, d: any) => {})
      .on('mouseover', (e, d: any) => {
        console.log('mouseover point', d);
      })
      .on('mouseout', (e, d: any) => {
        console.log('mouseout', d);
      });
  }

  fillZips = (colorByZip) => {
    let color = d3scale
      .scaleLinear()
      .domain([100, 50, 1])
      .range(<any>d3.schemeBlues[3]);
    this.g.selectAll('path').attr('fill', (d) => {
      return _.isUndefined(colorByZip[d.properties.zip])
        ? '#cccccc'
        : colorByZip[d.properties.zip];
    });
  };

  resetZoom = (d) => {
    this.svgStateMap
      .transition()
      .duration(750)
      .call(
        this.zoom.transform,
        d3zoom.zoomIdentity,
        d3zoom
          .zoomTransform(this.svgStateMap.node())
          .invert([this.width / 2, this.height / 2]),
      );
  };

  zoomLevel = 1;
  zoomInLocation = (location, zoom_level?) => {
    this.zoomLevel = this.zoomLevel + 1;
    this.zoomToLocation(location, this.zoomLevel);
  };
  zoomOutLocation = (location, zoom_level?) => {
    this.zoomLevel = this.zoomLevel - 1;
    if (this.zoomLevel <= 1) {
      this.zoomLevel = 1;
      this.resetZoom({});
    } else {
      this.zoomToLocation(location, this.zoomLevel);
    }
  };
  zoomToLocation = (location, zoom_level?) => {
    //const [x, y] = this.ypDealers.dealers[Math.floor(Math.random() * this.ypDealers.dealers.length)];
    const [x, y] = this.mapProjection([location.longitude, location.latitude]);
    let zoomLevel = _.isUndefined(zoom_level) ? 2 : zoom_level;
    this.svgStateMap
      .transition()
      .duration(2500)
      .call(
        this.zoom.transform,
        d3zoom.zoomIdentity
          .translate(this.width / 2, this.height / 2)
          .scale(zoomLevel)
          .translate(-x, -y),
      );
  };

  zoomIn = () => {
    this.svgStateMap.transition().call(this.zoom.scaleBy, 2);
  };
  zoomOut = () => {
    this.svgStateMap.transition().call(this.zoom.scaleBy, 0.5);
  };

  random = (d) => {
    const [x, y] =
      this.ypDealers.dealers[
        Math.floor(Math.random() * this.ypDealers.dealers.length)
      ];
    this.svgStateMap
      .transition()
      .duration(2500)
      .call(
        this.zoom.transform,
        d3zoom.zoomIdentity
          .translate(this.width / 2, this.height / 2)
          .scale(40)
          .translate(-x, -y),
      );
  };

  getAdjacentStatesByLocation(zip): Observable<any> {
    return this.efoEgi
      .getData('/elevate/getAdjacentStatesByLocation', { zip: zip })
      .pipe(
        map((rs) => {
          return _.map(rs, 'state');
        }),
      );
  }

  private statesByFip: any = [];

  getStatesByFip(): Observable<any> {
    if (!_.isEmpty(this.statesByFip)) {
      return of(this.statesByFip).pipe();
    } else {
      return this.efoEgi.getData('/elevate/topojson/statesByFip.json', {}).pipe(
        map((rs) => {
          this.statesByFip = rs;
          return rs;
        }),
      );
    }
  }

  private geojsonUsa = {};

  getGeoJsonUsa(): Observable<any> {
    if (!_.isEmpty(this.geojsonUsa)) {
      return of(this.geojsonUsa).pipe();
    } else {
      return this.efoEgi.getData('/elevate/geojson/us.json', {}).pipe(
        map((rs) => {
          this.geojsonUsa = rs;
          return rs;
        }),
      );
    }
  }
}
